import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { lockPageScroll, unlockPageScroll } from '../util/page-scroll-lock';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Constant } from '../constant';
import { MasterItem } from '../model/my-event';
import {
  EventSearchCondition,
  EventSearchConditionType,
  EventSearchOrder,
  EventSearchRequest,
  EventSearchShowChip,
  EventSearchShowCondition,
} from '../model/event-search';

interface GroupConfig {
  masterType: string;
  label: string;
  parentId?: () => number | null;
}

@Component({
  selector: 'app-event-search-filter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCheckboxModule,
    MatSelectModule,
    MatFormFieldModule,
  ],
  templateUrl: './event-search-filter.component.html',
  styleUrl: './event-search-filter.component.css',
})
export class EventSearchFilterComponent implements AfterViewChecked, OnDestroy {
  @Input() masterData: { [key: string]: MasterItem[] } = {};
  @Output() search = new EventEmitter<EventSearchRequest>();

  private readonly searchSubject = new Subject<EventSearchRequest>();
  private readonly searchSub: Subscription;

  public readonly Constant = Constant;

  public tabs: { title: string; types: EventSearchConditionType[] }[] = [
    {
      title: 'カテゴリー',
      types: [Constant.esConditionTypeCategoryMajor, Constant.esConditionTypeCategorySub],
    },
    {
      title: '条件を追加',
      types: [
        Constant.esConditionTypePurpose,
        Constant.esConditionTypeSocialTheme,
        Constant.esConditionTypeImproveState,
        Constant.esConditionTypeImproveOutcome,
      ],
    },
    {
      title: '対象等',
      types: [Constant.esConditionTypeOrganizerUnit, Constant.esConditionTypeParticipantCategory],
    },
    {
      title: 'キーワード',
      types: [Constant.esConditionTypeKeywordTitle, Constant.esConditionTypeKeywordSummary],
    },
    {
      title: '検索範囲',
      types: [Constant.esConditionTypeScopeOrg],
    },
  ];

  public purposeSidenav: { title: string; types: EventSearchConditionType[] }[] = [
    { title: '目的', types: [Constant.esConditionTypePurpose] },
    { title: '社会テーマ', types: [Constant.esConditionTypeSocialTheme] },
    { title: '改善指標', types: [Constant.esConditionTypeImproveState, Constant.esConditionTypeImproveOutcome] },
  ];

  public targetSidenav: { title: string; types: EventSearchConditionType[] }[] = [
    { title: '開催単位', types: [Constant.esConditionTypeOrganizerUnit] },
    { title: '対象者', types: [Constant.esConditionTypeParticipantCategory] },
  ];

  public orderOptions = Constant.esOrderSelect;

  public selectedTabIdx: number | null = null;
  public selectedSidenavIdx = 0;
  // SP（アコーディオン表示）で、選択中の見出しをもう一度押して閉じた状態
  public sidenavCollapsed = false;
  public panelLeft: number | null = 16;
  public panelRight: number | null = null;

  @ViewChild('tabNavWrapper') tabNavWrapperRef!: ElementRef<HTMLElement>;
  @ViewChild('filterContainer') filterContainerRef!: ElementRef<HTMLElement>;

  // SP でパネルを開いている間、ページのスクロールを止めているか
  private pageScrollLocked = false;

  public condition: EventSearchCondition[] = [];
  public showCondition: EventSearchShowCondition[] = [];

  public selectedCategoryMajorId: number | null = null;
  public scopeOrg: string | null = null;
  public order: EventSearchOrder = Constant.esOrderNewest;

  public keywordTitleInput = '';
  public keywordSummaryInput = '';

  private pendingScrollChipKey: string | null = null;

  constructor(private elementRef: ElementRef, private cdr: ChangeDetectorRef) {
    this.searchSub = this.searchSubject
      .pipe(debounceTime(300))
      .subscribe(request => this.search.emit(request));
  }

  ngAfterViewChecked(): void {
    this.updatePageScrollLock();
    if (!this.pendingScrollChipKey) {
      return;
    }
    const key = this.pendingScrollChipKey;
    const el = this.elementRef.nativeElement.querySelector(`[data-chip-key="${CSS.escape(key)}"]`);
    if (el) {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      this.pendingScrollChipKey = null;
    }
  }

  ngOnDestroy(): void {
    this.searchSub.unsubscribe();
    this.setPageScrollLock(false);
  }

  // SP（767px以下）でパネルを開いている間は、ページ自体のスクロールを止める。
  // iPhone ではページをスクロールすると、後ろの灰色の半透明の下端が一瞬ずれて白が見えるため（パネルの中はスクロールできる）
  private updatePageScrollLock(): void {
    const shouldLock = this.selectedTabIdx !== null && this.isSp();
    if (shouldLock !== this.pageScrollLocked) {
      this.setPageScrollLock(shouldLock);
    }
  }

  private setPageScrollLock(lock: boolean): void {
    if (lock === this.pageScrollLocked) {
      return;
    }
    this.pageScrollLocked = lock;
    // ダイアログ（iPhone・iPad）と同じ仕組みで止める（どちらかが閉じても、もう一方が開いている間は止めたままにする）
    if (lock) {
      lockPageScroll();
    } else {
      unlockPageScroll();
    }
  }

  // 追加したチップが見切れている場合にスクロールして表示する
  private scrollChipIntoView(type: EventSearchConditionType, id: number | string): void {
    this.pendingScrollChipKey = `${type}-${id}`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (document.querySelector('.cdk-overlay-backdrop')) {
      return;
    }
    // 並び替え等、検索条件の枠（検索条件の欄・タブ・パネル）以外の場所をクリックしたら閉じるようにする。
    // 検索条件の欄（選択中の条件のチップ）を押したときは閉じない
    const containerEl = this.filterContainerRef?.nativeElement;
    const clickedInside = !!containerEl && event.composedPath().includes(containerEl);
    if (this.selectedTabIdx !== null && !clickedInside) {
      this.selectedTabIdx = null;
    }
  }

  // =========================
  // マスタ取得
  // =========================
  getMaster(masterType: string): MasterItem[] {
    return this.masterData?.[masterType] || [];
  }

  getCategorySubMaster(): MasterItem[] {
    if (this.selectedCategoryMajorId == null) {
      return [];
    }
    return this.getMaster(Constant.esMasterTypeCategorySub)
      .filter(m => m.parentId === this.selectedCategoryMajorId);
  }

  // 「全体」は開催単位の選択肢に存在しないため除外する（対象組織の単位でのみ使用）
  getOrganizerUnitMaster(): MasterItem[] {
    return this.getMaster(Constant.esMasterTypeOrganizerUnit)
      .filter(m => m.name !== '全体');
  }

  // =========================
  // タブ / サイドナビ
  // =========================
  onTab(idx: number, event: MouseEvent): void {
    if (this.selectedTabIdx === idx) {
      this.selectedTabIdx = null;
      return;
    }
    this.selectedTabIdx = idx;
    this.selectedSidenavIdx = 0;
    // SP（アコーディオン表示）の「条件を追加」「対象等」は、すべて閉じた状態で開く
    this.sidenavCollapsed = this.isSp() && (idx === 1 || idx === 2);
    this.updatePanelPosition(event.currentTarget as HTMLElement);
    // パネルをいったん描画して幅を測り、タブの下に入りきらない場合は検索条件の枠の中央に置く。
    // 画面に表示される前に位置を決めるため、開いた直後に位置がずれて見えることはない
    this.cdr.detectChanges();
    this.fitPanelInWrapper();
  }

  // パネルを閉じる（パネルの後ろの半透明の黒を押したとき）
  closePanel(): void {
    this.selectedTabIdx = null;
  }

  private isSp(): boolean {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  // パネルがタブの左端から右に入りきらない場合は、検索条件の枠（タブの並び）の中央に置く。
  // パネルの幅は枠の幅まで（CSSの max-width: 100%）のため、中央に置けば必ず枠内に収まる
  private fitPanelInWrapper(): void {
    const wrapperEl = this.tabNavWrapperRef?.nativeElement;
    const panelEl = wrapperEl?.querySelector<HTMLElement>('.panel');
    if (!wrapperEl || !panelEl || this.panelLeft === null) {
      return;
    }
    const wrapperWidth = wrapperEl.offsetWidth;
    const panelWidth = panelEl.offsetWidth;
    if (this.panelLeft + panelWidth > wrapperWidth) {
      this.panelLeft = Math.max(0, Math.floor((wrapperWidth - panelWidth) / 2));
    }
  }

  // クリックしたタブの直下にパネルを表示する（タブごとに横幅が異なるため実測して位置決め）
  private updatePanelPosition(tabEl: HTMLElement): void {
    const wrapperEl = this.tabNavWrapperRef?.nativeElement;
    if (!wrapperEl || !tabEl) {
      return;
    }

    const tabLeft = tabEl.offsetLeft;

    // パネルはタブの左端にそろえて開く（入りきらない場合は fitPanelInWrapper で中央に置く）
    this.panelLeft = tabLeft;
    this.panelRight = null;
  }

  onSidenav(idx: number): void {
    // SP（アコーディオン表示）では、開いている見出しをもう一度押すと閉じる
    if (idx === this.selectedSidenavIdx && this.isSp()) {
      this.sidenavCollapsed = !this.sidenavCollapsed;
      return;
    }
    this.selectedSidenavIdx = idx;
    this.sidenavCollapsed = false;
  }

  // 選択中の大分類のすぐ下に中分類を置くための並び順（SPのアコーディオン表示用。PCでは大分類の一覧の右）
  getSelectedMajorOrder(): number {
    const idx = this.getMaster(Constant.esMasterTypeCategoryMajor)
      .findIndex(m => m.id === this.selectedCategoryMajorId);
    return Math.max(idx, 0) * 2 + 1;
  }

  // 選択中の大分類をもう一度押したら、選択を取り消す（中分類の選択も一緒に外れる）
  onCategoryMajorClick(id: number): void {
    if (this.selectedCategoryMajorId === id) {
      this.removeConditionGroup(Constant.esConditionTypeCategoryMajor);
    }
  }

  // 選択中の検索範囲をもう一度押したら、選択を取り消す
  onScopeClick(value: string): void {
    if (this.scopeOrg === value) {
      this.removeConditionGroup(Constant.esConditionTypeScopeOrg);
    }
  }

  // 指定した種類の条件を、チップの×を押したときと同じ手順で取り消す
  private removeConditionGroup(type: EventSearchConditionType): void {
    const groupIndex = this.showCondition.findIndex(sc => sc.type === type);
    if (groupIndex >= 0) {
      this.onRemoveChip(groupIndex, 0);
    }
  }

  tabHasSelection(idx: number): boolean {
    const types = this.tabs[idx].types;
    return this.showCondition.some(sc => types.includes(sc.type));
  }

  sidenavHasSelection(types: EventSearchConditionType[]): boolean {
    return this.showCondition.some(sc => types.includes(sc.type));
  }

  // =========================
  // 選択状態の判定
  // =========================
  isSelected(type: EventSearchConditionType, id: number): boolean {
    return this.condition.some(c => c.type === type && c.id === id);
  }

  // =========================
  // カテゴリー大分類
  // =========================
  onSelectCategoryMajor(id: number): void {
    if (this.selectedCategoryMajorId === id) {
      return;
    }
    this.condition = this.condition.filter(c =>
      c.type !== Constant.esConditionTypeCategoryMajor &&
      c.type !== Constant.esConditionTypeCategorySub
    );
    this.condition.push({ type: Constant.esConditionTypeCategoryMajor, id });
    this.selectedCategoryMajorId = id;

    this.rebuildGroup(Constant.esConditionTypeCategoryMajor);
    this.showCondition = this.showCondition.filter(sc => sc.type !== Constant.esConditionTypeCategorySub);
    this.scrollChipIntoView(Constant.esConditionTypeCategoryMajor, id);

    this.emitSearch();
  }

  // =========================
  // チェックボックス（複数選択）共通
  // =========================
  onToggleCondition(type: EventSearchConditionType, id: number, checked: boolean): void {
    if (checked) {
      this.condition.push({ type, id });
    } else {
      this.condition = this.condition.filter(c => !(c.type === type && c.id === id));
    }
    this.rebuildGroup(type);
    if (checked) {
      this.scrollChipIntoView(type, id);
    }
    this.emitSearch();
  }

  // =========================
  // 検索範囲（作成組織）
  // =========================
  onSelectScope(value: string): void {
    this.scopeOrg = value;
    this.condition = this.condition.filter(c => c.type !== Constant.esConditionTypeScopeOrg);
    this.condition.push({ type: Constant.esConditionTypeScopeOrg, id: value });

    const title = value === Constant.esScopeOwn ? '自組織のみ' : '他組織のみ';
    const group: EventSearchShowCondition = {
      type: Constant.esConditionTypeScopeOrg,
      connect: Constant.esConnectOr,
      data: [{ id: value, label: '検索範囲：', title }],
    };
    const existingIdx = this.showCondition.findIndex(sc => sc.type === Constant.esConditionTypeScopeOrg);
    if (existingIdx >= 0) {
      this.showCondition[existingIdx] = group;
    } else {
      this.showCondition.push(group);
    }
    this.scrollChipIntoView(Constant.esConditionTypeScopeOrg, value);

    this.emitSearch();
  }

  // =========================
  // キーワード
  // =========================
  // 追加したキーワード（入力欄の下に表示する。検索条件の欄のチップと同じデータ）
  getKeywordChips(type: EventSearchConditionType): EventSearchShowChip[] {
    return this.showCondition.find(sc => sc.type === type)?.data ?? [];
  }

  // 入力欄の下のキーワードの×：検索条件の欄の×と同じ手順で消す（両方から消える）
  onRemoveKeyword(type: EventSearchConditionType, chipIndex: number): void {
    const groupIndex = this.showCondition.findIndex(sc => sc.type === type);
    if (groupIndex >= 0) {
      this.onRemoveChip(groupIndex, chipIndex);
    }
  }

  keywordCount(type: EventSearchConditionType): number {
    return this.condition.filter(c => c.type === type).length;
  }

  // 最大件数に達していないか（ブランク／確定前の変換中でも押せるよう、入力内容のチェックは行わない）
  canAddKeyword(type: EventSearchConditionType): boolean {
    return this.keywordCount(type) < Constant.esKeywordMaxCount;
  }

  addKeyword(type: EventSearchConditionType): void {
    if (!this.canAddKeyword(type)) {
      return;
    }

    const isTitle = type === Constant.esConditionTypeKeywordTitle;
    // 半角/全角スペースも有効な検索ワードとして扱うためtrimしない
    const raw = isTitle ? this.keywordTitleInput : this.keywordSummaryInput;

    if (!raw) {
      // ブランクでの追加は何も反応させない（同一ワード追加時と同じ無反応挙動）
      return;
    }

    if (isTitle) {
      this.keywordTitleInput = '';
    } else {
      this.keywordSummaryInput = '';
    }

    if (this.condition.some(c => c.type === type && c.id === raw)) {
      return;
    }

    this.condition.push({ type, id: raw });

    const chip: EventSearchShowChip = { id: raw, label: '', title: raw };
    const existingIdx = this.showCondition.findIndex(sc => sc.type === type);
    if (existingIdx >= 0) {
      this.showCondition[existingIdx].data.push(chip);
    } else {
      const label = isTitle ? 'タイトル：' : '概要：';
      chip.label = label;
      this.showCondition.push({ type, connect: Constant.esConnectOr, data: [chip] });
    }
    this.scrollChipIntoView(type, raw);

    this.emitSearch();
  }

  // =========================
  // チップ削除
  // =========================
  onRemoveChip(groupIndex: number, chipIndex: number): void {
    const group = this.showCondition[groupIndex];
    if (!group) {
      return;
    }
    const chip = group.data[chipIndex];
    const type = group.type;

    this.condition = this.condition.filter(c => !(c.type === type && c.id === chip.id));

    if (type === Constant.esConditionTypeCategoryMajor) {
      this.selectedCategoryMajorId = null;
      this.condition = this.condition.filter(c => c.type !== Constant.esConditionTypeCategorySub);
      this.showCondition = this.showCondition.filter(sc =>
        sc.type !== Constant.esConditionTypeCategorySub &&
        sc.type !== Constant.esConditionTypeCategoryMajor
      );
    } else if (type === Constant.esConditionTypeScopeOrg) {
      this.scopeOrg = null;
      this.showCondition.splice(groupIndex, 1);
    } else if (type === Constant.esConditionTypeKeywordTitle || type === Constant.esConditionTypeKeywordSummary) {
      group.data.splice(chipIndex, 1);
      if (group.data.length === 0) {
        this.showCondition.splice(groupIndex, 1);
      } else if (chipIndex === 0) {
        // 先頭チップにのみ見出し（タイトル：／概要：）を持たせているため、
        // 先頭を削除した場合は新しい先頭チップに見出しを引き継ぐ
        group.data[0].label = type === Constant.esConditionTypeKeywordTitle ? 'タイトル：' : '概要：';
      }
    } else {
      this.rebuildGroup(type);
    }

    this.emitSearch();
  }

  onClearAll(): void {
    this.condition = [];
    this.showCondition = [];
    this.selectedCategoryMajorId = null;
    this.scopeOrg = null;
    this.keywordTitleInput = '';
    this.keywordSummaryInput = '';
    this.emitSearch();
  }

  // =========================
  // ソート
  // =========================
  get selectedOrderLabel(): string {
    return this.orderOptions.find(opt => opt.value === this.order)?.label ?? '';
  }

  onOrderChange(value: EventSearchOrder): void {
    this.order = value;
    this.emitSearch();
  }

  // =========================
  // 内部処理
  // =========================
  private getGroupConfig(type: EventSearchConditionType): GroupConfig {
    switch (type) {
      case Constant.esConditionTypeCategoryMajor:
        return { masterType: Constant.esMasterTypeCategoryMajor, label: 'カテゴリー：' };
      case Constant.esConditionTypeCategorySub:
        return { masterType: Constant.esMasterTypeCategorySub, label: '', parentId: () => this.selectedCategoryMajorId };
      case Constant.esConditionTypePurpose:
        return { masterType: Constant.esMasterTypePurpose, label: '目的：' };
      case Constant.esConditionTypeSocialTheme:
        return { masterType: Constant.esMasterTypeSocialTheme, label: '社会テーマ：' };
      case Constant.esConditionTypeImproveState:
        return { masterType: Constant.esMasterTypeImproveState, label: '状態の変化：' };
      case Constant.esConditionTypeImproveOutcome:
        return { masterType: Constant.esMasterTypeImproveOutcome, label: '企画の成果：' };
      case Constant.esConditionTypeOrganizerUnit:
        return { masterType: Constant.esMasterTypeOrganizerUnit, label: '開催単位：' };
      case Constant.esConditionTypeParticipantCategory:
        return { masterType: Constant.esMasterTypeParticipantCategory, label: '対象者：' };
      default:
        return { masterType: '', label: '' };
    }
  }

  // 検索ボックスで表示する選択肢の名前。企画の成果は「参加率（対象母数対比）」の「（…）」を外して短く表示する（検索まわりのみ）
  getSearchLabel(type: EventSearchConditionType, name: string): string {
    if (type === Constant.esConditionTypeImproveOutcome) {
      return name.replace(/（[^）]*）/g, '');
    }
    return name;
  }

  private rebuildGroup(type: EventSearchConditionType): void {
    const config = this.getGroupConfig(type);
    const parentId = config.parentId ? config.parentId() : null;
    const master = this.getMaster(config.masterType)
      .filter(m => !config.parentId || m.parentId === parentId);

    const selectedIds = new Set(this.condition.filter(c => c.type === type).map(c => c.id));

    const data: EventSearchShowChip[] = master
      .filter(m => selectedIds.has(m.id))
      .map((m, idx) => ({ id: m.id, label: idx === 0 ? config.label : '', title: this.getSearchLabel(type, m.name) }));

    const existingIdx = this.showCondition.findIndex(sc => sc.type === type);

    if (data.length === 0) {
      if (existingIdx >= 0) {
        this.showCondition.splice(existingIdx, 1);
      }
      return;
    }

    const group: EventSearchShowCondition = { type, connect: Constant.esConnectOr, data };

    if (existingIdx >= 0) {
      this.showCondition[existingIdx] = group;
    } else {
      const insertAt = type === Constant.esConditionTypeCategorySub
        ? this.showCondition.findIndex(sc => sc.type === Constant.esConditionTypeCategoryMajor) + 1
        : this.showCondition.length;
      this.showCondition.splice(Math.max(insertAt, 0), 0, group);
    }
  }

  private emitSearch(): void {
    this.searchSubject.next({
      conditions: [...this.condition],
      order: this.order,
    });
  }
}
